import {useEffect, useState} from 'react';
import {load} from 'exifreader';
import {useDriveCall} from '../drive/queries';
import {getFileMediaRange} from '../drive/driveApi';
import {getCachedThumbnail, loadThumbnail} from './blobCache';

interface UseThumbnailResult {
    url: string | undefined;
    isLoading: boolean;
}

// A JPEG's EXIF (APP1) segment — where the embedded preview thumbnail that cameras/editors
// write lives — is capped at ~64KB by the JPEG spec, so this comfortably covers it without
// downloading the whole photo.
const THUMBNAIL_FETCH_BYTES = 65536;

async function extractEmbeddedThumbnail(buffer: ArrayBuffer): Promise<Blob> {
    const tags = load(buffer, {expanded: true});
    const thumbnail = tags.Thumbnail;
    if (!thumbnail?.image) {
        throw new Error('Nem található beágyazott bélyegkép.');
    }
    return new Blob([thumbnail.image as ArrayBuffer], {type: 'image/jpeg'});
}

/**
 * Sets up a thumbnail URL for a file, using the following strategy:
 * 1. If the file has a `thumbnailLink` and it hasn't failed to load, use that directly (fastest).
 * 2. Otherwise, fetch the first ~64KB of the file and extract the embedded EXIF thumbnail (slower but reliable).
 * 
 * Resolves to nothing (caller shows a generic icon) if no
 * embedded thumbnail is found, e.g. for non-JPEG files.
 *
 * `enabled` gates the fetch so callers only pay this cost once the fast path has failed.
 */
export function useThumbnail(fileId: string, thumbnailLink: string | undefined, 
        enabled: boolean, thumbnailLinkFailed: boolean): UseThumbnailResult {
    const callDrive = useDriveCall();
    const cached = getCachedThumbnail(fileId);
    const [url, setUrl] = useState<string | undefined>(cached?.objectUrl);
    const [isLoading, setIsLoading] = useState(enabled && !cached);

    useEffect(() => {
        if (!enabled) return;

        const alreadyCached = getCachedThumbnail(fileId);
        if (alreadyCached) {
            setUrl(alreadyCached.objectUrl);
            setIsLoading(false);
            return;
        }

        // If we have a thumbnail link, we can use it directly
        if (thumbnailLink && !thumbnailLinkFailed) {
            setUrl(thumbnailLink);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        loadThumbnail(fileId, async () => {
            const buffer = await callDrive((token) => getFileMediaRange(token, fileId, THUMBNAIL_FETCH_BYTES));
            return extractEmbeddedThumbnail(buffer);
        })
            .then((entry) => {
                if (cancelled) return;
                setUrl(entry.objectUrl);
                setIsLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fileId, enabled, callDrive, thumbnailLinkFailed, thumbnailLink]);

    return {url, isLoading};
}
